import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import zionDebug from '@fs/zion-debug'
import { useQueryParams } from '@fs/zion-router'
import {
  generateSls,
  getNestedOrFloatingElementById,
  saveSLSActions,
  useSlsDispatchContext,
  useSlsStateContext,
  slsReducer,
  SlsContextInitialState,
} from '@fs/zion-sls'
import { InfoSheetPanel, useOverlay } from '@fs/zion-ui'
import { useR2DispatchContext, useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import PersonOverview from '../PersonOverview/PersonOverview'
import AddPersonGeneric from '../../../../components/generic/panels/addPersonPanel/AddPerson'
import RemovePerson from '../removePerson/RemovePerson'
import { headers, R2AppName } from '../../../../../shared/services/config'
import { errorCollector, generateDeletePersonDispatches } from '../../../../../shared/utils/utils'
import ErrorOverlay from '../../../../../shared/components/errorOverlay/ErrorOverlay'
import EditDanglingField from '../editPanels/EditDanglingField'
import EditOtherFields from '../editPanels/EditOtherFields'
import EditRelationships from '../editPanels/EditRelationships'
import EditAge from '../editPanels/EditAge'
import EditEssentialInfo from '../editPanels/EditEssentialInfo'
import HighlightPanelOld from '../../../../components/generic/panels/highlightPanelOld/HighlightPanelOld'
import SharedField from '../editPanels/sharedField/SharedField'
import SharedHighlightPanel from '../../../../components/generic/panels/sharedHighlightPanel/SharedHighlightPanel'
import SharedHighlightOverlay from '../../../../components/generic/overlays/SharedHighlightOverlay'
import useSlsHighlightsOnIndex from '../../hooks/useSlsHighlightsOnIndex'
import EventPanel from '../../../../components/generic/panels/eventPanel'
import useEventPanelOnIndex from '../../hooks/useEventPanelOnIndex'
import DragNames from '../selectPerson/dragNames/DragNames'
import LinkExistingEventPanel from '../../../../components/generic/panels/eventPanel/LinkExistingEventPanel'
import PersonOverviewV2 from '../personOverviewV2/PersonOverviewV2'
import UnsavedChangesDialog from '../../../../components/generic/unsavedChangesDialog/UnsavedChangesDialog'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'

const debug = zionDebug('rip:R2:IndexImage') // localStorage.debug = "fs:rip:R2:IndexImage"

export default function IndexImage() {
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const zionSlsState = useSlsStateContext()
  const zionSLSDispatch = useSlsDispatchContext()
  const dispatch = r2Dispatch
  const { viewIndexOverlay, closeOverlay } = useOverlayContext()
  const { removeQuery } = useQueryParams()
  const { inPanelStack } = useOverlayContext()
  const [saveError, setSaveError] = useState()
  const [personIdOverlay, setPersonIdOverlay] = useState(null)
  const [t] = useTranslation()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
    rip_sharedImageViewer_fullTextAttachFlow_v2: fullTextAttachFlowV2,
  } = useFeatureFlags([
    'rip_sharedImageViewer_saveChanges',
    'rip_sharedImageViewer_sls_actions_splunk_debugger',
    'rip_sharedImageViewer_fullTextAttachFlow_v2',
  ])
  const { sharedHighlightOverlay, splitScreenImageArk } = useSlsHighlightsOnIndex()
  const [isClickingBackArrow, setIsClickingBackArrow] = useState(false)

  const userClosedPanel = useCallback(() => {
    setIsClickingBackArrow(false)

    closeOverlay('viewIndex')
    removeQuery('personArk')
    removeQuery('action')
  }, [closeOverlay, removeQuery])

  const setSelectedPerson = (personId) => {
    if (inPanelStack('editRelationships')) {
      setPersonIdOverlay(personId)
    }
    r2Dispatch({ type: 'SET_CREATED_PERSON_ID', value: personId })
  }

  function saveSls(slsActions, deletePersonDispatches) {
    if (saveChangesFlag.isOn) {
      saveSLSActions({
        actions: slsActions,
        userId: r2State.userId,
        appName: R2AppName,
        fsUserAgentChain: headers['FS-User-Agent-Chain'],
        splunkDebugModeArguments: slsActionsSplunkDebugger.isOn ? { theFileFromWhichSaveWasCalled: 'IndexImage' } : {},
      })
        .then(() => {
          zionSLSDispatch({
            type: 'BATCH_DISPATCHES',
            value: deletePersonDispatches,
          })
        })
        .catch((err) => {
          setSaveError(err)
          errorCollector(err, zionSlsState?.currentImageArk, {
            codeId: 77,
            slsActions,
            status: err?.response?.status,
            responseBody: err?.response?.data,
          })
        })
    }
  }

  const handleDeletePerson = async (personIds) => {
    const deletePersonDispatches = generateDeletePersonDispatches(
      personIds,
      zionSlsState.currentImageArk,
      zionSlsState.images
    )

    const slsActions = generateSls(deletePersonDispatches)
    saveSls(slsActions, deletePersonDispatches)
  }

  const { selectedPerson, event } = useEventPanelOnIndex()

  debug('Render: ', { r2State, zionSlsState, selectedPersonId: r2State.selectedPersonId })

  const shouldSaveToSlsAfterNewPerson = !['EditRelationshipsPanel'].includes(r2State.addPersonPanelArgs?.referrer) // Add other panels that should not save to SLS

  const handlingNewlyAddedPersonForRelationships = useCallback(
    (data) => {
      // We use the data returned from the AddPerson panel to create a temporary SLS person object,
      // which is then used in the EditRelationships panel. To create this SLS person object,
      // we need to generate a temporary SLS state containing only the current image and the record
      // to which the person was added.
      const reducerState = slsReducer(
        {
          ...SlsContextInitialState,
          // We need to create a new base image array with the current image only and a record because the slsReducer will need those
          // to properly create the SLS person object
          images: [
            {
              elementType: 'IMAGE',
              partitionKey: zionSlsState.currentImageArk,
              subElements: [
                {
                  elementType: 'RECORD',
                  partitionKey: zionSlsState.currentImageArk,
                  id: r2State.selectedRecordId,
                  subElements: [],
                },
              ],
            },
          ],
          types: zionSlsState.types,
        },
        {
          type: 'BATCH_DISPATCHES',
          value: [
            ...(data?.dispatches?.personDispatches || []),
            ...(data?.dispatches?.nameDispatches || []),
            { type: 'SET_RECORD_PERSON_IDS', value: r2State.selectedRecordId },
          ],
        }
      )

      const createdPerson = getNestedOrFloatingElementById(reducerState.images, data.personId)

      // Update R2 context with the data about the newly added person
      r2Dispatch({
        type: 'SET_TEMPORARY_PERSONS_DATA_FOR_RELATIONSHIPS',
        value: { id: r2State.addPersonPanelArgs?.metadata?.id, data: { ...data, slsPerson: createdPerson } },
      })
    },
    [r2Dispatch, r2State.addPersonPanelArgs, zionSlsState.currentImageArk, r2State.selectedRecordId, zionSlsState.types]
  )

  // Unsaved changes logic for new flow in R2
  const [showUnsavedChangesOverlay, setShowUnsavedChangesOverlay] = useState(false)
  const unsavedChangesOverlay = useOverlay()

  // Show the unsaved changes overlay when the user tries to switch images
  useUnsavedChangesOverlay({
    overlay: unsavedChangesOverlay,
    panelId: 'viewIndex',
    condition: showUnsavedChangesOverlay,
  })

  const handleDirtyChange = useCallback((isDirty) => {
    setShowUnsavedChangesOverlay(isDirty)
  }, [])

  const handleClose = useCallback(
    (triggerEvent) => {
      setIsClickingBackArrow(true)

      // If theres a trigger event (like clicking the back arrow button ), show the unsaved changes overlay.
      // If there isn't a trigger event (like programmatically closing the panel because the info sheet is closed),
      // just close the panel without showing the overlay
      if (showUnsavedChangesOverlay && triggerEvent) {
        unsavedChangesOverlay.open()
      } else {
        userClosedPanel()
      }
    },
    [showUnsavedChangesOverlay, unsavedChangesOverlay, userClosedPanel]
  )

  return (
    <>
      {!fullTextAttachFlowV2.isOn && (
        <PersonOverview handleDeletePerson={handleDeletePerson} key={r2State.selectedPersonId} />
      )}
      {fullTextAttachFlowV2.isOn && (
        <InfoSheetPanel
          headingColor="green"
          title={t('person-overview-v2.title', `View Name`)}
          {...{ ...viewIndexOverlay, close: handleClose }}
        >
          <PersonOverviewV2
            handleDeletePerson={handleDeletePerson}
            key={r2State.selectedPersonId}
            onDirtyChange={handleDirtyChange}
          />
          <UnsavedChangesDialog
            overlay={unsavedChangesOverlay}
            onBack={() => {
              setIsClickingBackArrow(false)
              unsavedChangesOverlay.close()
            }}
            onExit={() => {
              unsavedChangesOverlay.close()
              // If there's a pending action, run it after confirming exit, e.g., navigate away or close panel
              if (typeof unsavedChangesOverlay.pendingAction === 'function' && !isClickingBackArrow) {
                unsavedChangesOverlay.pendingAction()
              } else {
                userClosedPanel()
              }
            }}
          />
        </InfoSheetPanel>
      )}
      {/** Don't load panel unless they are needed.  Performance optimization and we don't want them changing state when not open  */}
      {inPanelStack('addPersonPage1Generic') && (
        <AddPersonGeneric
          userId={r2State.userId}
          noRecord={r2State.createdPersonNoRecord}
          onGetPerson={setSelectedPerson}
          splitScreenArk={splitScreenImageArk}
          appName={R2AppName}
          shouldSaveToSls={shouldSaveToSlsAfterNewPerson}
          onSave={shouldSaveToSlsAfterNewPerson ? null : handlingNewlyAddedPersonForRelationships}
          recordId={r2State.addPersonPanelArgs?.recordId}
        />
      )}
      {inPanelStack('dragNames') && <DragNames />}
      {inPanelStack('editOtherFields') && <EditOtherFields />}
      {inPanelStack('editRelationships') && (
        <EditRelationships personIdOverlay={personIdOverlay} setPersonIdOverlay={setPersonIdOverlay} />
      )}
      {inPanelStack('editAge') && <EditAge />}
      {inPanelStack('eventPanel') && (
        <EventPanel
          person={selectedPerson}
          event={event}
          splitScreenImageArk={splitScreenImageArk}
          shouldParseEventfromSls
          shouldSavetoSls
          shouldSaveHighlights
          userId={r2State.userId}
          viewInContextHandler={(pageNumber) =>
            r2Dispatch({
              type: 'OPEN_SPLIT_SCREEN_TO_PAGE',
              value: pageNumber,
            })
          }
          useSharedEvent
          appName={R2AppName}
        />
      )}
      {inPanelStack('linkExistingEvent') && <LinkExistingEventPanel person={selectedPerson} appName={R2AppName} />}
      {inPanelStack('editEssentialInfo') && <EditEssentialInfo />}
      {(inPanelStack('editSharedFieldPage1') || inPanelStack('editSharedFieldPage2')) && <SharedField />}
      {inPanelStack('highlightsPanelOld') && <HighlightPanelOld splitScreenImageArk={splitScreenImageArk} />}
      {inPanelStack('editDanglingField') && <EditDanglingField />}
      {inPanelStack('highlightsPanelOld') &&
        (inPanelStack('editSharedHighlightPage1') || inPanelStack('editSharedHighlightPage2')) && (
          <SharedHighlightPanel selectedPersonId={r2State.selectedPersonId} />
        )}
      {inPanelStack('removePerson') && (
        <RemovePerson
          handleDeletePerson={handleDeletePerson}
          handleHoverPerson={(person) => {
            dispatch({ type: 'SET_HIGHLIGHTED_PERSON_ID', value: person?.id })
          }}
          handleUnHoverPerson={() => {
            dispatch({ type: 'SET_HIGHLIGHTED_PERSON_ID', value: null })
          }}
        />
      )}
      <ErrorOverlay
        error={saveError}
        setError={setSaveError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
      <SharedHighlightOverlay overlay={sharedHighlightOverlay} />
    </>
  )
}
